class InfiniteScroll {
  constructor() {
    // Grid elements
    this.productGrid = document.querySelector('[data-infinite-scroll="true"]');
    this.productGridContainer = document.querySelector('[data-infinite-scroll-container="true"]');
    this.subcategoryGrids = document.querySelectorAll('[data-infinite-scroll-subcategory]');
    this.multiCategoryGrids = document.querySelectorAll('.collection ul.grid.product-grid');
    this.collectionContainer = document.querySelector('.collection');
    
    // Determine layout type
    this.isAccessoriesTemplate = !!this.productGridContainer;
    this.isMultiCategoryLayout = this.multiCategoryGrids.length > 1;
    
    // Exit if no valid grid
    if (!this.productGrid && !this.isAccessoriesTemplate && !this.isMultiCategoryLayout) return;
    
    // Pagination & loading state
    this.nextUrl = document.querySelector('.infinite-scroll-page-link')?.dataset.nextUrl;
    this.loadingIndicator = document.querySelector('.infinite-scroll-indicator');
    this.isLoading = false;
    this.reachedEnd = !this.nextUrl;
    this.consecutiveEmptyLoads = 0;
    this.maxConsecutiveEmptyLoads = 3;
    
    // Filter tracking
    this.currentFilterId = this.getFilterStateId();
    this.loadedProducts = new Map(); // Category -> Set of product IDs
    this.allLoadedProducts = new Set();
    this.lastFilterState = this.getFilterState();
    
    // Bind methods
    this.handleScroll = this.debounce(this.handleScroll.bind(this), 150);
    this.handleResize = this.debounce(this.checkScroll.bind(this), 300);
    this.handleFacetClick = this.handleFacetClick.bind(this);
    this.resetInfiniteScroll = this.debounce(this.resetInfiniteScroll.bind(this), 500);
    
    // Initialize tracking and events
    this.initializeProductTracking();
    this.init();
  }
  
  init() {
    // Exit if invalid state
    if ((!this.isAccessoriesTemplate && !this.productGrid && !this.isMultiCategoryLayout) || 
        (this.isAccessoriesTemplate && !this.productGridContainer) || 
        this.reachedEnd) return;
    
    // Setup event listeners
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('click', this.handleFacetClick);
    
    // Listen for form submissions (filter form)
    document.addEventListener('submit', (event) => {
      if (event.target.closest('facet-filters-form, #FacetFiltersForm')) {
        setTimeout(this.resetInfiniteScroll, 500);
      }
    });
    
    // Setup mutation observer
    this.setupMutationObserver();
    
    // Listen for Shopify filter events
    ['facets:updated', 'collection:reloaded', 'filters:updated'].forEach(event => {
      document.addEventListener(event, this.resetInfiniteScroll);
    });
    
    // Initial check
    setTimeout(() => this.checkScroll(), 500);
  }
  
  initializeProductTracking() {
    // Track products in standard grid
    if (this.productGrid) {
      this.trackProductsInGrid(this.productGrid, `main-${this.currentFilterId}`);
    }
    
    // Track products in accessory subcategories
    if (this.isAccessoriesTemplate && this.subcategoryGrids) {
      this.subcategoryGrids.forEach(grid => {
        const subcategory = grid.dataset.infiniteScrollSubcategory || 'unknown';
        this.trackProductsInGrid(grid, `${subcategory}-${this.currentFilterId}`);
      });
    }
    
    // Track products in multi-category layout
    if (this.isMultiCategoryLayout && this.multiCategoryGrids) {
      this.multiCategoryGrids.forEach((grid, index) => {
        this.trackProductsInGrid(grid, `category-${index}-${this.currentFilterId}`);
      });
    }
  }
  
  trackProductsInGrid(grid, categoryId) {
    if (!grid) return;
    
    // Initialize category set
    if (!this.loadedProducts.has(categoryId)) {
      this.loadedProducts.set(categoryId, new Set());
    }
    
    // Add all products in grid to tracking
    const items = grid.querySelectorAll('.grid__item');
    const categoryProducts = this.loadedProducts.get(categoryId);
    
    items.forEach(item => {
      const productId = this.getProductIdFromGridItem(item);
      if (productId) {
        categoryProducts.add(productId);
        this.allLoadedProducts.add(productId);
      }
    });
  }
  
  setupMutationObserver() {
    if (!this.collectionContainer) return;
    
    // Create mutation observer
    this.observer = new MutationObserver(this.debounce(() => {
      const currentFilterState = this.getFilterState();
      
      // Reset if filter state changed
      if (this.lastFilterState !== currentFilterState) {
        this.lastFilterState = currentFilterState;
        this.resetInfiniteScroll();
      }
    }, 300));
    
    // Start observing collection container
    this.observer.observe(this.collectionContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-id']
    });
    
    // Also observe facet filters if they exist
    const facetsContainer = document.querySelector('#main-collection-filters, .facets-container');
    if (facetsContainer) {
      this.observer.observe(facetsContainer, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }
  }
  
  getFilterState() {
    // Create string representation of filter state
    const urlState = window.location.search;
    const activeFilters = document.querySelectorAll('.active-facets__button').length;
    const sortBy = document.querySelector('#SortBy')?.value || '';
    return `${urlState}|${activeFilters}|${sortBy}`;
  }
  
  getFilterStateId() {
    // Create hash ID from current filters
    const params = new URLSearchParams(window.location.search);
    const filterParts = [];
    
    // Add filter parameters
    for (const [key, value] of params.entries()) {
      if (key.startsWith('filter.') || key === 'sort_by') {
        filterParts.push(`${key}=${value}`);
      }
    }
    
    // Add facet button count
    const activeFacets = document.querySelectorAll('.active-facets__button').length;
    if (activeFacets > 0) {
      filterParts.push(`facets=${activeFacets}`);
    }
    
    // Return 'default' if no filters
    if (filterParts.length === 0) return 'default';
    
    // Create hash
    return this.hashString(filterParts.sort().join('|'));
  }
  
  hashString(str) {
    // Create a simple hash
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36).substring(0, 8);
  }
  
  updateGridReferences() {
    // Update grid references
    if (this.isAccessoriesTemplate) {
      this.subcategoryGrids = document.querySelectorAll('[data-infinite-scroll-subcategory]');
    } else {
      this.multiCategoryGrids = document.querySelectorAll('.collection ul.grid.product-grid');
      this.isMultiCategoryLayout = this.multiCategoryGrids.length > 1;
      this.productGrid = document.querySelector('[data-infinite-scroll="true"]');
    }
    
    // Update container references
    this.collectionContainer = document.querySelector('.collection');
    this.productGridContainer = document.querySelector('#product-grid');
    
    // Update pagination reference
    this.nextUrl = document.querySelector('.infinite-scroll-page-link')?.dataset.nextUrl;
    this.reachedEnd = !this.nextUrl;
  }
  
  handleFacetClick(event) {
    // Check for filter interaction
    const isFilterElement = event.target.closest(
      '[data-facet-toggle], .facets__display, .facet-checkbox, ' + 
      '.active-facets__button, [data-filter], [type="checkbox"][name^="filter."]'
    );
    
    if (isFilterElement) {
      // Hide loading indicator immediately
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
      
      // Reset after a short delay
      setTimeout(this.resetInfiniteScroll, 500);
    }
  }
  
  resetInfiniteScroll() {
    // Wait for any ongoing fetch operations to complete
    if (this.isLoading) {
      setTimeout(this.resetInfiniteScroll, 200);
      return;
    }
    
    // Generate a new filter state ID
    this.currentFilterId = this.getFilterStateId();
    
    // Reset state
    this.isLoading = false;
    this.consecutiveEmptyLoads = 0;
    
    // Update grid references
    this.updateGridReferences();
    
    // Reset product tracking
    this.loadedProducts = new Map();
    this.allLoadedProducts = new Set();
    this.initializeProductTracking();
    
    // Hide loading indicator if needed
    if (!this.nextUrl && this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
      this.reachedEnd = true;
    }
    
    // Check if we need to load more content
    setTimeout(() => this.checkScroll(), 500);
  }
  
  handleScroll() {
    if (this.isLoading || this.reachedEnd) return;
    this.checkScroll();
  }
  
  checkScroll() {
    // Double check grid refs are valid
    if (
      (!this.productGrid && !this.isAccessoriesTemplate && !this.isMultiCategoryLayout) ||
      (this.isAccessoriesTemplate && (!this.subcategoryGrids || this.subcategoryGrids.length === 0)) ||
      (this.isMultiCategoryLayout && (!this.multiCategoryGrids || this.multiCategoryGrids.length === 0))
    ) {
      this.updateGridReferences();
    }
    
    // Exit if invalid state
    if (
      (!this.productGrid && !this.isAccessoriesTemplate && !this.isMultiCategoryLayout) ||
      (this.isAccessoriesTemplate && (!this.subcategoryGrids || this.subcategoryGrids.length === 0)) ||
      (this.isMultiCategoryLayout && (!this.multiCategoryGrids || this.multiCategoryGrids.length === 0)) ||
      !this.nextUrl
    ) {
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
      this.reachedEnd = true;
      return;
    }
    
    // Check scroll position
    const scrollPosition = window.innerHeight + window.pageYOffset;
    const documentHeight = document.body.offsetHeight;
    const threshold = documentHeight - 1000; // Load 1000px before bottom
    
    // Load if near bottom or page is very short
    if (scrollPosition >= threshold || documentHeight < window.innerHeight * 1.5) {
      this.loadNextPage();
    }
  }
  
  async loadNextPage() {
    if (this.isLoading || this.reachedEnd || !this.nextUrl) return;
    
    this.isLoading = true;
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'flex';
    }
    
    try {
      // Build URL with filters
      let url = new URL(this.nextUrl, window.location.origin);
      let currentParams = new URLSearchParams(window.location.search);
      
      if (currentParams.toString()) {
        // Combine pagination with filters
        let nextParams = new URLSearchParams(url.search);
        let page = nextParams.get('page');
        
        if (page) {
          currentParams.set('page', page);
        }
        
        url = new URL(`${url.pathname}?${currentParams.toString()}`, window.location.origin);
      }
      
      // Fetch next page
      const response = await fetch(url.toString(), {
        headers: {'X-Requested-With': 'XMLHttpRequest'}
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      
      // Parse response
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      
      // Verify filter state hasn't changed
      const currentFilterId = this.getFilterStateId();
      if (currentFilterId !== this.currentFilterId) {
        throw new Error('Filter state changed');
      }
      
      // Process based on template type
      let newItemsAdded = 0;
      
      if (this.isAccessoriesTemplate) {
        newItemsAdded = this.processAccessoriesTemplate(doc);
      } else if (this.isMultiCategoryLayout) {
        newItemsAdded = this.processMultiCategoryLayout(doc);
      } else {
        newItemsAdded = this.processStandardGrid(doc);
      }
      
      // Get next URL for pagination
      const nextUrlElement = doc.querySelector('.infinite-scroll-page-link');
      this.nextUrl = nextUrlElement ? nextUrlElement.dataset.nextUrl : null;
      
      // Hide loading indicator
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
      
      // Handle end cases
      if (!this.nextUrl || newItemsAdded === 0) {
        if (newItemsAdded === 0) {
          this.consecutiveEmptyLoads++;
        } else {
          this.consecutiveEmptyLoads = 0;
        }
        
        if (!this.nextUrl || this.consecutiveEmptyLoads >= this.maxConsecutiveEmptyLoads) {
          this.reachedEnd = true;
        }
      }
      
      // Initialize scripts if new content was added
      if (newItemsAdded > 0) {
        this.reinitializeScripts();
        setTimeout(() => this.checkScroll(), 250);
      }
    } catch (error) {
      // Just make sure loading state is cleaned up
    } finally {
      this.isLoading = false;
      
      // Always hide loading indicator
      if (this.loadingIndicator) {
        this.loadingIndicator.style.display = 'none';
      }
      
      if (!this.nextUrl) {
        this.reachedEnd = true;
      }
    }
  }

  processMultiCategoryLayout(doc) {
    let totalNewItems = 0;
    
    // Find all category grids in the loaded document
    const newCategoryGrids = doc.querySelectorAll('.collection ul.grid.product-grid');
    
    // Update our grid references to make sure we're working with the latest DOM
    this.multiCategoryGrids = document.querySelectorAll('.collection ul.grid.product-grid');
    
    if (this.multiCategoryGrids.length === 0) {
      console.warn('No multi-category grids found in current document');
      return 0;
    }
    
    // Process each category grid separately, matching by index
    this.multiCategoryGrids.forEach((currentGrid, index) => {
      // Get the corresponding grid from the loaded document
      const newGrid = index < newCategoryGrids.length ? newCategoryGrids[index] : null;
      
      if (!newGrid) return;
      
      // Get or create the category product set with filter ID
      const categoryId = `category-${index}-${this.currentFilterId}`;
      if (!this.loadedProducts.has(categoryId)) {
        this.loadedProducts.set(categoryId, new Set());
      }
      
      // Add products from this category only
      const categoryNewItems = this.processGridItems(newGrid, currentGrid, categoryId);
      console.log(`Added ${categoryNewItems} new items to category grid ${index} with filter ID: ${this.currentFilterId}`);
      totalNewItems += categoryNewItems;
    });
    
    // Handle any new category sections
    if (newCategoryGrids.length > this.multiCategoryGrids.length) {
      console.log(`Found ${newCategoryGrids.length - this.multiCategoryGrids.length} new category grids`);
      const newCategoriesAdded = this.processNewCategories(newCategoryGrids, this.multiCategoryGrids.length);
      totalNewItems += newCategoriesAdded;
    }
    
    return totalNewItems;
  }
  
  processGridItems(sourceGrid, targetGrid, categoryId) {
    // Get all product items from the source grid
    const newItems = Array.from(sourceGrid.querySelectorAll('.grid__item'));
    let addedItems = 0;
    
    // Get category product set
    const categoryProducts = this.loadedProducts.get(categoryId);
    if (!categoryProducts) {
      console.warn(`No product set found for category ID: ${categoryId}`);
      return 0;
    }
    
    // Process each item
    newItems.forEach(item => {
      const productId = this.getProductIdFromGridItem(item);
      
      // Skip if no product ID or already in this category
      if (!productId || categoryProducts.has(productId)) {
        return;
      }
      
      // Add to tracking for this category
      categoryProducts.add(productId);
      this.allLoadedProducts.add(productId);
      
      // Add to grid
      const clone = item.cloneNode(true);
      targetGrid.appendChild(clone);
      addedItems++;
    });
    
    return addedItems;
  }
  
  processNewCategories(newCategoryGrids, startIndex) {
    // Find the container where we should add new categories
    const collectionContainer = this.collectionContainer || document.querySelector('.collection');
    if (!collectionContainer) return 0;
    
    let totalNewItems = 0;
    
    // Create any new categories
    for (let i = startIndex; i < newCategoryGrids.length; i++) {
      const newGrid = newCategoryGrids[i];
      const categoryId = `category-${i}-${this.currentFilterId}`;
      
      // Initialize tracking for this new category
      if (!this.loadedProducts.has(categoryId)) {
        this.loadedProducts.set(categoryId, new Set());
      }
      
      // Find the heading for this category
      let headingElement = this.findCategoryHeading(newGrid);
      
      // Clone the grid (empty)
      const gridClone = newGrid.cloneNode(false);
      
      // Get all product items
      const newItems = Array.from(newGrid.querySelectorAll('.grid__item'));
      let categoryNewItems = 0;
      const categoryProducts = this.loadedProducts.get(categoryId);
      
      // Add items to the new grid
      newItems.forEach(item => {
        const productId = this.getProductIdFromGridItem(item);
        
        // Skip if no product ID or already loaded in any category
        if (!productId || categoryProducts.has(productId)) return;
        
        // Add to tracking
        categoryProducts.add(productId);
        this.allLoadedProducts.add(productId);
        
        // Add to grid
        const itemClone = item.cloneNode(true);
        gridClone.appendChild(itemClone);
        categoryNewItems++;
      });
      
      // Only add non-empty grids
      if (categoryNewItems > 0) {
        // Add heading if we found one
        if (headingElement) {
          const headingClone = headingElement.cloneNode(true);
          collectionContainer.appendChild(headingClone);
        }
        
        // Add the grid to the page
        collectionContainer.appendChild(gridClone);
        console.log(`Added new category ${categoryId} with ${categoryNewItems} products`);
        totalNewItems += categoryNewItems;
      }
    }
    
    // Update our grid references
    this.multiCategoryGrids = document.querySelectorAll('.collection ul.grid.product-grid');
    return totalNewItems;
  }
  
  findCategoryHeading(grid) {
    // Look for a heading before this grid
    let element = grid.previousElementSibling;
    let attempts = 0;
    
    while (element && attempts < 5) {
      if (element.tagName && ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(element.tagName)) {
        return element;
      }
      element = element.previousElementSibling;
      attempts++;
    }
    
    return null;
  }
  
  processAccessoriesTemplate(doc) {
    let totalNewItems = 0;
    
    // Update references
    this.subcategoryGrids = document.querySelectorAll('[data-infinite-scroll-subcategory]');
    
    if (this.subcategoryGrids.length === 0) {
      console.warn('No subcategory grids found in current document');
      return 0;
    }
    
    // Process existing subcategories
    this.subcategoryGrids.forEach(grid => {
      const subcategory = grid.dataset.infiniteScrollSubcategory;
      
      // Find matching subcategory in loaded document
      const newGrid = doc.querySelector(`[data-infinite-scroll-subcategory="${subcategory}"]`);
      if (!newGrid) return;
      
      // Get or create the subcategory product set with filter ID
      const categoryId = `${subcategory}-${this.currentFilterId}`;
      if (!this.loadedProducts.has(categoryId)) {
        this.loadedProducts.set(categoryId, new Set());
      }
      
      // Process items for this subcategory
      const subcategoryNewItems = this.processGridItems(newGrid, grid, categoryId);
      console.log(`Added ${subcategoryNewItems} new items to subcategory ${subcategory} with filter ID: ${this.currentFilterId}`);
      totalNewItems += subcategoryNewItems;
    });
    
    // Handle new subcategories
    const docSubcategories = doc.querySelectorAll('[data-infinite-scroll-subcategory]');
    docSubcategories.forEach(newGrid => {
      const subcategory = newGrid.dataset.infiniteScrollSubcategory;
      
      // Skip existing subcategories
      const existingGrid = Array.from(this.subcategoryGrids).find(grid => 
        grid.dataset.infiniteScrollSubcategory === subcategory
      );
      
      if (existingGrid || !this.productGridContainer) return;
      
      // Initialize tracking for this new subcategory with filter ID
      const categoryId = `${subcategory}-${this.currentFilterId}`;
      if (!this.loadedProducts.has(categoryId)) {
        this.loadedProducts.set(categoryId, new Set());
      }
      
      // Get the category product set
      const categoryProducts = this.loadedProducts.get(categoryId);
      
      // Find heading and create new grid
      const heading = newGrid.previousElementSibling;
      if (heading && heading.tagName === 'H2') {
        // Clone heading and empty grid
        const headingClone = heading.cloneNode(true);
        const gridClone = newGrid.cloneNode(false);
        
        // Add items to the new grid
        const newItems = Array.from(newGrid.querySelectorAll('.grid__item'));
        let subcategoryNewItems = 0;
        
        newItems.forEach(item => {
          const productId = this.getProductIdFromGridItem(item);
          
          // Skip if already in this subcategory
          if (!productId || categoryProducts.has(productId)) return;
          
          // Add to tracking
          categoryProducts.add(productId);
          this.allLoadedProducts.add(productId);
          
          // Add to grid
          const itemClone = item.cloneNode(true);
          gridClone.appendChild(itemClone);
          subcategoryNewItems++;
        });
        
        // Only add non-empty subcategories
        if (subcategoryNewItems > 0) {
          this.productGridContainer.appendChild(headingClone);
          this.productGridContainer.appendChild(gridClone);
          
          console.log(`Added new subcategory ${subcategory} with ${subcategoryNewItems} products with filter ID: ${this.currentFilterId}`);
          totalNewItems += subcategoryNewItems;
          
          // Update subcategory references
          this.subcategoryGrids = document.querySelectorAll('[data-infinite-scroll-subcategory]');
        }
      }
    });
    
    return totalNewItems;
  }
  
  processStandardGrid(doc) {
    // Check if our product grid still exists (might have changed after filtering)
    this.productGrid = document.querySelector('[data-infinite-scroll="true"]');
    
    if (!this.productGrid) {
      console.warn('Standard product grid not found, checking for multi-category layout');
      // If standard grid no longer exists, check if we've switched to multi-category layout
      this.updateGridReferences();
      
      if (this.isMultiCategoryLayout) {
        return this.processMultiCategoryLayout(doc);
      }
      
      return 0;
    }
    
    // Get or create the main category product set with filter ID
    const categoryId = `main-${this.currentFilterId}`;
    if (!this.loadedProducts.has(categoryId)) {
      this.loadedProducts.set(categoryId, new Set());
    }
    
    // Find new products in the loaded document
    const newGrid = doc.querySelector('#product-grid');
    if (!newGrid) {
      console.warn('No product grid found in loaded document');
      return 0;
    }
    
    // Process items for the main grid
    const newItems = this.processGridItems(newGrid, this.productGrid, categoryId);
    console.log(`Added ${newItems} new items to standard grid with filter ID: ${this.currentFilterId}`);
    
    return newItems;
  }
  
  getProductIdFromGridItem(item) {
    // Try data attribute
    let productId = item.getAttribute('data-product-id');
    if (productId) return productId;
    
    // Try form elements
    const form = item.querySelector('form[action*="/cart/add"]');
    if (form) {
      // Try form ID
      const formId = form.id;
      if (formId && formId.startsWith('product-form-')) {
        return formId.replace('product-form-', '');
      }
      
      // Try input value
      const idInput = form.querySelector('input[name="id"]');
      if (idInput && idInput.value) {
        return idInput.value;
      }
      
      // Try form attribute
      productId = form.getAttribute('data-product-id');
      if (productId) return productId;
    }
    
    // Try product link
    const productLink = item.querySelector('a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href');
      const match = href.match(/\/products\/([^/?#]+)/);
      if (match && match[1]) {
        return match[1]; // Product handle
      }
    }
    
    // Create fallback ID from content
    const text = item.innerText.trim().substring(0, 50);
    return 'item-' + this.hashString(text + Date.now());
  }
  
  reinitializeScripts() {
    // Quick add
    if (typeof QuickAdd !== 'undefined') {
      try { new QuickAdd(); } catch (e) {}
    }
    
    // Quick add bulk
    if (typeof QuickAddBulk !== 'undefined') {
      try { new QuickAddBulk(); } catch (e) {}
    }
    
    // Payment button
    if (window.Shopify && window.Shopify.PaymentButton) {
      try { window.Shopify.PaymentButton.init(); } catch (e) {}
    }
    
    // Notify listeners
    document.dispatchEvent(new CustomEvent('infinite-scroll:page-loaded', {
      detail: { filterState: this.currentFilterId }
    }));
  }
  
  debounce(func, wait) {
    let timeout;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }
  
  destroy() {
    // Remove event listeners
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('click', this.handleFacetClick);
    
    // Remove filter event listeners
    ['facets:updated', 'collection:reloaded', 'filters:updated'].forEach(event => {
      document.removeEventListener(event, this.resetInfiniteScroll);
    });
    
    // Disconnect observer
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  window.infiniteScroll = new InfiniteScroll();
});

// Re-initialize on section load
document.addEventListener('shopify:section:load', (event) => {
  const hasGrid = event.target.querySelector('[data-infinite-scroll="true"]') || 
                  event.target.querySelector('[data-infinite-scroll-container="true"]') || 
                  event.target.querySelectorAll('.collection ul.grid.product-grid').length > 0;
  
  if (hasGrid) {
    if (window.infiniteScroll?.destroy) {
      window.infiniteScroll.destroy();
    }
    window.infiniteScroll = new InfiniteScroll();
  }
});

// Re-initialize on facet updates
document.addEventListener('facets:updated', () => {
  setTimeout(() => {
    if (window.infiniteScroll?.destroy) {
      window.infiniteScroll.destroy();
    }
    window.infiniteScroll = new InfiniteScroll();
  }, 300);
}); 